import * as React from "react";
import { useEffect, useState } from "react";
import Centre from "../../../domain/Centre";
import { UUID } from "../../../domain/UUID";
import { Alert, Card, CardContent, CardHeader, Chip, Stack, Typography } from "@mui/material";
import Student from "../../../domain/Student";
import { getStudents, removeStudent } from "../../../gateway/students";
import AddStudentForm from "./AddStudentForm";
import CentreStudentsTableDataGrid, { StudentTableEditMode } from "./CentreStudentsTableDataGrid";
import Season from "../../../domain/Season";
import ExamLevel from "../../../domain/ExamLevel";
import IconCardTitle from "../../common/IconCardTitle";
import GroupIcon from '@mui/icons-material/Group';
import DownloadIcon from "@mui/icons-material/Download";
import LoadingButton from "@mui/lab/LoadingButton";
import SeasonState from "../../../domain/SeasonState";
import { downloadAllCentreCertificates, getResults } from "../../../gateway/results";
import StudentExamResult from "../../../domain/StudentExamResult";
import CentreResultsDownloadButtons from "./CentreResultsDownloadButtons";

interface Props {
  readonly season: Season,
  readonly centreId: UUID<Centre>,
  readonly readOnly?: boolean
}

export default function BfiStudentsCard({ season, centreId, readOnly = false }: Props) {
  const [students, setStudents] = useState<Student[] | undefined>(undefined);
  const [results, setResults] = useState<{ [key: UUID<Student>]: StudentExamResult } | undefined>(undefined);
  const [downloadInProgress, setDownloadInProgress] = useState(false);

  const advancedExam = season.exams[ExamLevel.ADVANCED];
  // Older seasons may not have every BFI level
  const bfiExams = ExamLevel.bfiValues().map(level => season.exams[level]).filter(exam => exam !== undefined);
  const registrationOpen = !readOnly && SeasonState.isBefore(season.state, SeasonState.COMPLETE);
  const showResults = SeasonState.isOrIsAfter(season.state, SeasonState.ROUND_1_RESULTS_PUBLICATION);
  const canUnregister = SeasonState.isBefore(season.state, SeasonState.ROUND_1_RESULTS_PUBLICATION);

  useEffect(() => {
    getStudents({ centreId, seasonId: season.id }).then(allStudents => {
      const bfiStudents = allStudents.filter(s => s.examId !== advancedExam.examId);
      setStudents(bfiStudents);
    });
  }, [season.id, centreId, advancedExam.examId]);

  useEffect(() => {
    if (showResults) {
      getResults({ centreId }).then(allResults => {
        // Filter to only BFI results for this season
        const bfiExamIds = new Set(bfiExams.map(e => e.examId));
        const bfiResults: { [key: UUID<Student>]: StudentExamResult } = {};
        Object.entries(allResults).forEach(([studentId, result]) => {
          if (bfiExamIds.has(result.examId)) {
            bfiResults[studentId] = result;
          }
        });
        setResults(bfiResults);
      });
    }
  }, [season.id, centreId, showResults, advancedExam.examId]);

  const addNewStudent = (student: Student) => {
    if (students === undefined) {
      setStudents([student]);
      return;
    }

    setStudents([student, ...students]);
  }

  const deleteStudent = (id: UUID<Student>) => {
    removeStudent(id);

    if (students === undefined) {
      return;
    }

    setStudents(students.filter(s => s.id !== id));
  }

  const handleStudentUpdated = (updatedStudent: Student) => {
    if (students) {
      setStudents(students.map(student =>
        student.id === updatedStudent.id ? updatedStudent : student
      ));
    }
  };

  if (students === undefined) {
    return <Typography>Loading</Typography>;
  }

  if (readOnly && students.length === 0) {
    return null;
  }

  const editMode = readOnly
    ? StudentTableEditMode.NOT_EDITABLE
    : canUnregister
      ? StudentTableEditMode.REGISTRATION_EDITABLE
      : StudentTableEditMode.DETAILS_EDITABLE;

  let multipleStudentsHaveAwards = false;
  if (results !== undefined) {
    const studentsWithAwards = Object.values(results).filter((result) => result.award !== null);
    multipleStudentsHaveAwards = studentsWithAwards.length > 1;
  }

  const handleDownloadAllCertificates = () => {
    setDownloadInProgress(true);
    downloadAllCentreCertificates(season.id, centreId, `UKLO_${season.year}_BFI_certificates`, bfiExams.map(e => e.examId))
      .finally(() => setDownloadInProgress(false));
  };

  return (
    <Card variant="outlined">
      <CardHeader
        title={
          <Stack direction="row" spacing={1} alignItems="center">
            <IconCardTitle icon={<GroupIcon fontSize="large" />} text={`BFI students for ${season.year}`} />
            {!readOnly && (
              <Chip
                label={registrationOpen ? "Registration open" : "Registration closed"}
                color={registrationOpen ? "success" : "default"}
                size="small"
              />
            )}
          </Stack>
        }
        action={multipleStudentsHaveAwards && (
          <LoadingButton
            loading={downloadInProgress}
            variant="contained"
            onClick={handleDownloadAllCertificates}
            startIcon={<DownloadIcon />}
            sx={{ mr: 1 }}
          >
            Download all certificates
          </LoadingButton>
        )}
        sx={{ pb: 0 }}
      />
      <CardContent>
        <Stack spacing={1}>
          {registrationOpen && (
            <AddStudentForm
              centreId={centreId}
              onNewStudent={addNewStudent}
              allowedExamLevels={ExamLevel.bfiValues()}
            />
          )}
          {students.length > 0
            ? <CentreStudentsTableDataGrid
                season={season}
                rows={students}
                editMode={editMode}
                displayRegistered={registrationOpen && SeasonState.isBefore(season.state, SeasonState.ROUND_1_NON_ADVANCED_TEST_PERIOD)}
                results={showResults ? results : undefined}
                onDeleteStudent={deleteStudent}
                onStudentUpdated={handleStudentUpdated}
              />
            : <Alert severity="info">No BFI students registered.</Alert>
          }
          {showResults && results && Object.keys(results).length > 0 && (
            <CentreResultsDownloadButtons
              centreId={centreId}
              exams={bfiExams}
              results={results}
              season={season}
            />
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
